import React, { useMemo } from "react";
import { Pause, Play } from "lucide-react";
import { useAudioPlayer } from "./GlobalAudioPlayer";
import { modelVoices } from "@/utils/tools";
import { useT } from "@jammable/ui-core/src/i18n";

// Fixed bar heights so server and client render the same waveform.
const WAVE = [8, 14, 20, 12, 24, 16, 10, 22, 18, 9, 15, 26, 12, 19, 8, 14];

// modelVoices names are "Male Singing 1" etc.; shown translated.
const demoLabel = (t, name) => {
  const m = /^(Male|Female) Singing (\d+)$/.exec(name || "");
  if (!m) return name;
  return t(
    m[1] === "Female" ? "voice.demos.femaleSinging" : "voice.demos.maleSinging",
    { n: m[2] },
  );
};

// Male voices first for a male voice, female first for a female one, each
// in modelVoices order. A plain function (not state set in an effect) so the
// server renders the demos too: they used to appear only after hydration,
// so the HTML Google fetched had no demos at all.
export function sortedDemoVoices(gender) {
  const isFemale = (v) => v.name.toLowerCase().includes("female");
  const first = gender === "female" ? isFemale : (v) => !isFemale(v);
  return [
    ...modelVoices.filter((v) => first(v)),
    ...modelVoices.filter((v) => !first(v)),
  ];
}

const TryVoiceSection = ({ modelData }) => {
  const t = useT();
  const sortedVoices = useMemo(
    () => sortedDemoVoices(modelData?.tts?.gender),
    [modelData?.tts?.gender],
  );
  const { playAudio, info, pauseAudio, isPlaying } = useAudioPlayer();

  const hasSuccessfulDemo = modelData?.demos?.some(
    (d) => d?.status !== "failed",
  );

  if (!hasSuccessfulDemo) {
    return null;
  }

  return (
    <section className="tw-flex tw-w-full tw-flex-col tw-gap-3.5">
      <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
        <h2 className="jam-display tw-m-0 tw-text-[22px] tw-font-extrabold tw-text-jam-ink md:tw-text-[28px]">
          {t("voice.demos.title")}
        </h2>
        <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {t("voice.demos.note")}
        </span>
      </div>
      <div className="tw-grid tw-grid-cols-2 tw-gap-2 md:tw-gap-2.5">
        {sortedVoices?.map((demo) => {
          const isDemo = modelData?.demos?.find((d) => d?.name === demo?.param);
          const playing =
            isPlaying && info?.id === `${modelData?._id}-${demo?.name}`;
          const label = demoLabel(t, demo?.name);

          // Only demos that exist: a missing one had a play button that did
          // nothing.
          return !isDemo?.convertedUrl || isDemo?.status === "failed" ? null : (
            <div
              key={demo?.name}
              className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-gap-2 tw-rounded-[20px] tw-bg-jam-lav tw-p-2.5 md:tw-gap-3 md:tw-rounded-[24px] md:tw-p-3"
            >
              <button
                type="button"
                aria-label={
                  playing
                    ? t("voice.pause", { name: label })
                    : t("voice.play", { name: label })
                }
                onClick={() => {
                  if (playing) {
                    pauseAudio();
                  } else {
                    playAudio({
                      audio: isDemo?.convertedUrl,
                      info: {
                        image: modelData?.image?.url,
                        name: label,
                        id: `${modelData?._id}-${demo?.name}`,
                        artistNames: modelData?.artistname,
                      },
                    });
                  }
                }}
                className="tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-white md:tw-h-11 md:tw-w-11"
              >
                {playing ? (
                  <Pause size={14} fill="currentColor" strokeWidth={0} />
                ) : (
                  <Play size={14} fill="currentColor" strokeWidth={0} />
                )}
              </button>
              {/* The real file, for crawlers and assistive tech; the button
                  above plays it through the site-wide player. preload="none":
                  nothing downloads until someone presses play. */}
              <audio src={isDemo.convertedUrl} preload="none" hidden />
              <div className="tw-min-w-0 tw-flex-grow">
                <div className="tw-truncate tw-text-xs tw-font-extrabold tw-text-jam-ink md:tw-text-sm">
                  {label}
                </div>
                <div className="tw-hidden tw-text-xs tw-font-semibold tw-text-jam-muted md:tw-block">
                  {t("voice.demos.english")}
                </div>
              </div>
              {/* Decorative waveform, desktop only. */}
              <div
                aria-hidden
                className="tw-hidden tw-h-7 tw-items-center tw-gap-0.5 xl:tw-flex"
              >
                {WAVE.map((h, i) => (
                  <span
                    key={i}
                    style={{ height: h }}
                    className={`tw-w-[3px] tw-rounded-sm ${playing ? "tw-bg-jam-purple" : "tw-bg-jam-soft"}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default TryVoiceSection;
